import { useCallback } from 'react';
import { Notification } from '@/common/utils';
import { buildFinancialInstitutionAnswers } from '@/modules/financials/utils';

const useFinancialInstitutionSubmit = ({
  closeModal,
  decedentId,
  deletedInstitutionIds,
  institutions,
  institutionQuestion,
  isEdit,
  parentAnswer,
  parentQuestion,
  refreshTask,
  setInstitutions,
  submitAnswersMutation,
  t,
}) =>
  useCallback(
    async (event) => {
      event.preventDefault();

      let hasErrors = false;
      const nextInstitutions = institutions.map((institution) => {
        const trimmedName = institution.name.trim();

        if (!trimmedName) {
          hasErrors = true;

          return {
            ...institution,
            error: t('Please fill {{label}}', { label: t('Name') }),
          };
        }

        return {
          ...institution,
          name: trimmedName,
          error: '',
        };
      });

      setInstitutions(nextInstitutions);

      if (hasErrors) {
        return;
      }

      if (!institutionQuestion?.id) {
        Notification.error(t('Failed to create financial institution'));
        return;
      }

      try {
        const hasInstitutionToActivate = nextInstitutions.some(
          (institution) => !institution.answerId || institution.restoredFromArchive
        );
        const answers = buildFinancialInstitutionAnswers({
          institutions: nextInstitutions,
          deletedInstitutionIds,
          questionId: institutionQuestion.id,
          parentQuestion,
          parentAnswer,
          parentQuestionValue:
            !nextInstitutions.length && !!deletedInstitutionIds.length
              ? 'No'
              : hasInstitutionToActivate
              ? 'Yes'
              : null,
        });

        if (!answers.length) {
          closeModal();
          return;
        }

        await submitAnswersMutation.mutateAsync({
          answers,
          decedentId,
          section: 'main',
          sectionType: 'Question',
        });

        Notification.success(
          t(
            isEdit
              ? 'Financial institutions updated successfully'
              : 'Financial institutions created successfully'
          )
        );
        await refreshTask?.();
        closeModal();
      } catch {
        Notification.error(t('Failed to create financial institution'));
      }
    },
    [
      closeModal,
      decedentId,
      deletedInstitutionIds,
      institutions,
      institutionQuestion,
      isEdit,
      parentAnswer,
      parentQuestion,
      refreshTask,
      setInstitutions,
      submitAnswersMutation,
      t,
    ]
  );

export default useFinancialInstitutionSubmit;
